import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import GridExcel from "src/components/common/gridWrapper/Grid_Excel";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";

import {
  defaultPRD2050_R01Dto,
  PRD2050_R01Dto,
} from "./Dto_PRD2050_R01";
import { usePRD2050_R01Store } from "./Store_PRD2050_R01";

const PRD2050_R01_ORDER_COLUMN_STATE_KEY =
  "PRD2050_R01-order-grid-column-state-v2";

interface TablePRD2050_R01Props {
  gridRef: RefObject<AgGridReact<PRD2050_R01Dto> | null>;
  onSelectOrder: (productOrder: PRD2050_R01Dto) => Promise<boolean>;
  onOpenMaterialLot: (productOrder: PRD2050_R01Dto) => Promise<void>;
}

/**
 * 조회된 작업지시 목록을 표시한다.
 * 단일 클릭은 상세 조회, 더블 클릭은 투입 LOT 선택 모달 열기로 구분한다.
 */
export const Table_PRD2050_R01 = ({
  gridRef,
  onSelectOrder,
  onOpenMaterialLot,
}: TablePRD2050_R01Props) => {
  const {
    PRD2050_R01List,
    selectedPRD2050_R01,
    columnState,
    setColumnState,
    scrollTop,
    setScrollTop,
  } = usePRD2050_R01Store();
  const { workPlaceList, workProcessList, jobTeamList } = useCommonCodeStore();
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  // 더블 클릭 발생 여부를 판별할 수 있도록 단일 클릭 실행을 잠시 지연한다.
  const rowClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 공통 그리드 로직으로 행 식별자, 선택 색상 규칙, 빈 영역 채움 데이터를 만든다.
  const { getRowId, createRowClassRules, paddedGridData } =
    useGridSettingContent<PRD2050_R01Dto>({
      gridRef,
      key: "productOrderNo",
      gridData: PRD2050_R01List,
      defaultGridData: defaultPRD2050_R01Dto,
      selectedRow: selectedPRD2050_R01,
    });

  const headerNames = useMemo<Record<string, string>>(
    () => ({
      rowIndex: "",
      productOrderNo: "작업지시번호",
      orderDt: "작업일자",
      workplaceCd: "작업공정",
      workProcess: "공정유형",
      jobTeam: "작업팀",
      orderEmp: "작업자",
      orderEmpNm: "작업자명",
      itemCd: "품목코드",
      itemNm: "품명",
      itemSpec: "규격",
      itemUnit: "단위",
      orderQty: "수량",
      resultQty: "생산량",
    }),
    [],
  );

  const baseColumnDefs = useMemo<ColDef<PRD2050_R01Dto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        colId: "productOrderNo",
        headerName: "작업지시번호",
        field: "productOrderNo",
        width: 150,
      },
      {
        colId: "orderDt",
        headerName: "작업일자",
        field: "orderDt",
        width: 110,
        valueFormatter: (params) =>
          params.value ? formatDate(new Date(params.value)) : "",
      },
      {
        colId: "workplaceCd",
        headerName: "작업공정",
        field: "workplaceCd",
        width: 140,
        valueFormatter: (params) =>
          workPlaceList.find((item) => item.workplaceCd === params.value)
            ?.workplaceNm ?? params.value,
      },
      {
        colId: "workProcess",
        headerName: "공정유형",
        field: "workProcess",
        width: 90,
        valueFormatter: (params) =>
          workProcessList.find((item) => item.minorCd === params.value)
            ?.codeNm ?? params.value,
      },
      {
        colId: "jobTeam",
        headerName: "작업팀",
        field: "jobTeam",
        width: 70,
        valueFormatter: (params) =>
          jobTeamList.find((item) => item.minorCd === params.value)?.codeNm ??
          params.value,
      },
      { colId: "orderEmp", headerName: "작업자", field: "orderEmp", width: 90 },
      {
        colId: "orderEmpNm",
        headerName: "작업자명",
        field: "orderEmpNm",
        width: 90,
      },
      { colId: "itemCd", headerName: "품목코드", field: "itemCd", width: 130 },
      { colId: "itemNm", headerName: "품명", field: "itemNm", width: 140 },
      { colId: "itemSpec", headerName: "규격", field: "itemSpec", width: 120 },
      { colId: "itemUnit", headerName: "단위", field: "itemUnit", width: 60 },
      {
        colId: "orderQty",
        headerName: "수량",
        field: "orderQty",
        width: 80,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "",
      },
      {
        colId: "resultQty",
        headerName: "생산량",
        field: "resultQty",
        width: 80,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "",
      },
    ],
    [jobTeamList, workPlaceList, workProcessList],
  );

  const defaultColumnState = useMemo(
    () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs],
  );

  const columnDefs = useMemo(
    () =>
      GridCustomColumnDefs<PRD2050_R01Dto>({
        baseColumnDefs,
        columnState: columnState?.some((state) => state.colId === "rowIndex")
          ? columnState
          : null,
        headerNames,
        fallbackHeaderClass: aggridHeaderClass,
      }) as ColDef<PRD2050_R01Dto>[],
    [baseColumnDefs, columnState, headerNames],
  );

  const rowClassRules = useMemo(
    () =>
      createRowClassRules<PRD2050_R01Dto>({
        idKey: "productOrderNo",
        selectedList: selectedPRD2050_R01
          ? [selectedPRD2050_R01.productOrderNo]
          : [],
        deletedList: [],
      }),
    [createRowClassRules, selectedPRD2050_R01],
  );

  /** 200ms 안에 더블 클릭이 없을 때만 단일 클릭 상세 조회를 실행한다. */
  const handleRowClick = useCallback(
    (productOrder: PRD2050_R01Dto) => {
      if (rowClickTimerRef.current) {
        clearTimeout(rowClickTimerRef.current);
      }
      rowClickTimerRef.current = setTimeout(() => {
        rowClickTimerRef.current = null;
        void onSelectOrder(productOrder);
      }, 200);
    },
    [onSelectOrder],
  );

  /** 대기 중인 단일 클릭을 취소하고 모달 열기 흐름만 실행한다. */
  const handleRowDoubleClick = useCallback(
    async (productOrder: PRD2050_R01Dto) => {
      if (rowClickTimerRef.current) {
        clearTimeout(rowClickTimerRef.current);
        rowClickTimerRef.current = null;
      }
      await onOpenMaterialLot(productOrder);
    },
    [onOpenMaterialLot],
  );

  // 화면이 닫힐 때 남아 있는 클릭 타이머를 정리한다.
  useEffect(
    () => () => {
      if (rowClickTimerRef.current) {
        clearTimeout(rowClickTimerRef.current);
      }
    },
    [],
  );

  return (
    <GridContextWrapper<PRD2050_R01Dto>
      gridRef={gridRef}
      gridWrapperRef={gridWrapperRef}
      paddedGridData={paddedGridData}
      baseColumnDefs={columnDefs}
      defaultColumnState={defaultColumnState}
      setColumnState={setColumnState}
      localStorageKey={PRD2050_R01_ORDER_COLUMN_STATE_KEY}
      enableDataHistory={false}
      enableCellCalculation
    >
      <GridExcel<PRD2050_R01Dto>
        ref={gridRef}
        rowData={paddedGridData}
        getRowId={getRowId}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowClassRules={rowClassRules}
        onRowClicked={(event) => event.data && handleRowClick(event.data)}
        onRowDoubleClicked={async (event) => {
          if (!event.data) return;
          await handleRowDoubleClick(event.data);
        }}
        columnStateKey={PRD2050_R01_ORDER_COLUMN_STATE_KEY}
        setColumnState={setColumnState}
        getDefaultColumnState={() => defaultColumnState}
        scrollTop={scrollTop}
        setScrollTop={setScrollTop}
      />
    </GridContextWrapper>
  );
};
